import { movementOf, signedText, type IntelDelta } from '../model/intel-delta.js';
import { DATA, DATA_LABEL, DATA_TAG, READOUT } from '../../theme/type.js';

/**
 * The turn-delta dock (design's delta block).
 *
 * ## No delta is drawn from one report
 *
 * `againstTurn` is null in three ways — turn 1 has no turn 0, the older report
 * has not been fetched, the fetch is still out — and the panel says so with the
 * turn it *would* compare against absent from the heading, drawing the figures as
 * plain counts with **no sign, no arrow and no colour**. A `+0` where there is
 * nothing to compare to is the single most convincing wrong number this screen
 * could produce: it reads exactly like a quiet turn.
 *
 * ## No VP (F1)
 *
 * The design's delta block has four VP bindings. §12's evaluator is M6, so every
 * one of them is a number this build cannot compute, and M5-D13 reserves the
 * chrome slot rather than filling it. There is no VP row here, no placeholder,
 * and nothing that would need re-laying out when M6 lands.
 *
 * ## What the five measures are
 *
 * `ENTRIES`, `CRITICAL` and `PRIORITY` are counts over the report; `DETECTION` is
 * the detection-ladder traffic; `ESCALATION` is the **movement** the turn's own
 * `ESCALATION_*` entries add up to — signed, because a decay takes the counter
 * down and the payload's `amount` says only how far. The counter's *value* is the
 * status row's meter and is not repeated here (say a thing once per screen).
 */

export interface DeltaPanelProps {
  readonly delta: IntelDelta;
}

export function DeltaPanel(props: DeltaPanelProps): JSX.Element {
  const { delta } = props;
  const comparing = delta.againstTurn !== null;

  return (
    <div
      data-testid="intel-delta"
      data-comparing={comparing}
      data-mat="panel"
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        borderTop: '1px solid var(--border)',
        background: 'var(--surface)',
      }}
    >
      <div
        data-mat="chrome hdr"
        style={{
          ...DATA_LABEL,
          flex: 'none',
          height: 27,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 12px',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
          fontSize: 10.5,
          color: 'var(--text-2)',
        }}
      >
        <span>DELTA</span>
        {/* `--warn` is licensed by a real protocol reading, and "there is no
            turn before this one" is not one — it is guaranteed on every game's
            turn 1. Both branches read as plain fact, same as the measures'
            own no-predecessor cells below. */}
        <span
          data-testid="intel-delta-basis"
          style={{ fontWeight: 400, color: 'var(--text-muted)' }}
        >
          {comparing
            ? `TURN ${pad(delta.turn)} AGAINST ${pad(delta.againstTurn ?? 0)}`
            : 'NO PRIOR REPORT'}
        </span>
      </div>

      <div
        style={{
          flex: 'none',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0 1px',
          background: 'var(--border)',
        }}
      >
        {delta.measures.map((measure) => (
          <Cell
            key={measure.key}
            testId="intel-delta-measure"
            label={measure.key}
            now={measure.now}
            move={movementOf(measure)}
            signedNow={measure.key === 'ESCALATION'}
          />
        ))}
      </div>

      <div
        data-testid="intel-delta-categories"
        style={{
          flex: 'none',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 14px',
          padding: '7px 12px 8px',
        }}
      >
        {delta.categories.map((category) => {
          const move = movementOf(category);
          return (
            <span
              key={category.category}
              data-testid="intel-delta-category"
              data-category={category.category}
              style={{ ...DATA, display: 'inline-flex', gap: 6, fontSize: 10.5 }}
            >
              <span style={{ color: 'var(--text-muted)' }}>{category.category}</span>
              <span style={{ color: 'var(--text-2)' }}>{category.now}</span>
              {/* `0` when compared and unchanged, **nothing** when there is no
                  predecessor. Drawing nothing for both put "the same as last turn"
                  and "nothing to compare to" on the same pixels — the must-not,
                  one row down from where it is stated. */}
              {move !== null && (
                <span
                  data-testid="intel-delta-category-move"
                  style={{ color: move > 0 ? 'var(--warn)' : 'var(--text-muted)' }}
                >
                  {signedText(move)}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function Cell(props: {
  testId: string;
  label: string;
  now: number;
  /** Null where there is no predecessor. **Never rendered as zero.** */
  move: number | null;
  signedNow: boolean;
}): JSX.Element {
  return (
    <div
      data-testid={props.testId}
      data-measure={props.label}
      data-compared={props.move !== null}
      style={{
        flex: '1 1 96px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '7px 12px 8px',
        background: 'var(--surface)',
      }}
    >
      <span style={{ ...DATA_TAG, fontSize: 9.5, color: 'var(--text-muted)' }}>{props.label}</span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
        {/* Same compact READOUT variant as the header's turn number — see
            IntelView's comment on why the letter-spacing departs from the
            masthead's 23px readout. */}
        <span
          data-testid="intel-delta-now"
          style={{ ...READOUT, fontSize: 15, letterSpacing: '.04em', color: 'var(--text)' }}
        >
          {props.signedNow ? signedText(props.now) : props.now}
        </span>
        {props.move !== null && (
          <span
            data-testid="intel-delta-move"
            style={{
              ...DATA,
              fontSize: 11,
              color: props.move > 0 ? 'var(--warn)' : 'var(--text-muted)',
            }}
          >
            {signedText(props.move)}
          </span>
        )}
      </span>
    </div>
  );
}

function pad(turn: number): string {
  return String(turn).padStart(2, '0');
}
